import {orderProductsByMarketAvailability} from 'nutshell-core/order-products-by-market-availability';
import type {Market, MarketId, ProductId} from 'nutshell-core/types';

import type {Option} from 'shells/select-picker-generic/types';

import type {ProductPickerProduct} from './graphql/helpers/product-picker-product';

const PRODUCT_SEARCH_LIMIT = 50;
const PRODUCT_HANDOFF_TTL_MS = 5000;
const PRODUCT_HANDOFF_CACHE_LIMIT = 20;
const selectedProductHandoffCache = new Map<
    string,
    {product: ProductPickerProduct; expiresAt: number}
>();

export type ProductOption = Option & {
    subtitle: string;
    sublabel: string;
    product: ProductPickerProduct;
    isDisabled?: boolean;
};

export type ProductOptionsContext = {
    currentMarket: Market | null | undefined;
    excludeProductIds: ProductId[];
    selectedId: ProductId | null | undefined;
    selectedProduct: ProductPickerProduct | null;
};

export function getProductCacheKey(productId: ProductId, marketId?: MarketId): string {
    return `${productId}:${marketId ?? 'no-market'}`;
}

export function cacheSelectedProductForHandoff(
    product: ProductPickerProduct,
    marketId?: MarketId
): void {
    const now = Date.now();
    selectedProductHandoffCache.forEach(({expiresAt}, cacheKey) => {
        if (expiresAt <= now) selectedProductHandoffCache.delete(cacheKey);
    });

    const cacheKey = getProductCacheKey(product.id, marketId);
    selectedProductHandoffCache.delete(cacheKey);
    selectedProductHandoffCache.set(cacheKey, {
        product,
        expiresAt: now + PRODUCT_HANDOFF_TTL_MS,
    });

    if (selectedProductHandoffCache.size > PRODUCT_HANDOFF_CACHE_LIMIT) {
        const oldestProductId = selectedProductHandoffCache.keys().next().value;
        if (oldestProductId) selectedProductHandoffCache.delete(oldestProductId);
    }
}

export function getSelectedProductHandoff(
    productId: ProductId,
    marketId?: MarketId
): ProductPickerProduct | undefined {
    const cacheKey = getProductCacheKey(productId, marketId);
    const handoff = selectedProductHandoffCache.get(cacheKey);
    if (!handoff) return undefined;

    if (handoff.expiresAt <= Date.now()) {
        selectedProductHandoffCache.delete(cacheKey);
        return undefined;
    }

    return handoff.product;
}

export function getProductOptions(
    products: ProductPickerProduct[],
    {currentMarket, excludeProductIds, selectedId, selectedProduct}: ProductOptionsContext
): ProductOption[] {
    const marketId = currentMarket?.id;
    const excludedIds = new Set(excludeProductIds.filter((id) => id !== selectedId));
    const uniqueProducts = new Map<ProductId, ProductPickerProduct>();

    products.forEach((product) => {
        if (excludedIds.has(product.id)) return;
        if (marketId && product.id !== selectedId && !product.prices[marketId]) return;

        uniqueProducts.set(product.id, product);
    });

    if (selectedProduct && selectedProduct.id === selectedId) {
        uniqueProducts.set(selectedProduct.id, selectedProduct);
    }

    let filteredProducts = Array.from(uniqueProducts.values());
    if (
        selectedProduct &&
        selectedProduct.id === selectedId &&
        filteredProducts.length > PRODUCT_SEARCH_LIMIT
    ) {
        filteredProducts = filteredProducts
            .filter((product) => product.id !== selectedId)
            .slice(0, PRODUCT_SEARCH_LIMIT - 1)
            .concat(selectedProduct);
    } else {
        filteredProducts = filteredProducts.slice(0, PRODUCT_SEARCH_LIMIT);
    }

    const orderedProducts = marketId
        ? orderProductsByMarketAvailability(filteredProducts, marketId)
        : filteredProducts;

    return orderedProducts.map((product: ProductPickerProduct & {isDisabled?: boolean}) => ({
        label: product.name,
        value: product.id,
        subtitle: product.sku || '',
        sublabel: product.category?.name || 'No category',
        product,
        isDisabled: product.isDisabled,
    }));
}
